import { useCallback, useEffect, useState } from "react";

/**
 * usePwaInstall — abstracts the PWA installation experience.
 *
 * Returns:
 *   - canInstall: boolean — true when the browser fired `beforeinstallprompt`
 *                 (Android Chrome / Edge / desktop Chrome). On iOS Safari this
 *                 will always be false because Apple uses manual "Add to Home
 *                 Screen" — we surface `isIos` so the UI can show the manual
 *                 instructions instead.
 *   - isIos: boolean — running iOS Safari (or a non-standalone iOS browser).
 *   - isStandalone: boolean — already installed and running as a PWA.
 *   - install(): triggers the native prompt (Android-style only).
 *   - dismiss(): user clicked "later" — we'll stop nagging for 30 days.
 */
const DISMISS_KEY = "companin_pwa_dismissed";
const DISMISS_DAYS = 30;

function detectIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPadOS 13+ pretends to be macOS, so also check for touch + 'iPad' in UA
  const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const iPadOS =
    navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOS || iPadOS;
}

function detectStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isDismissedRecently() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export function usePwaInstall() {
  const [deferred, setDeferred] = useState(null);
  const [isStandalone, setIsStandalone] = useState(detectStandalone);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
      setIsStandalone(true);
    };
    const onDisplay = () => setIsStandalone(detectStandalone());

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    window.matchMedia?.("(display-mode: standalone)").addEventListener?.("change", onDisplay);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      window.matchMedia?.("(display-mode: standalone)").removeEventListener?.("change", onDisplay);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return { outcome: "unavailable" };
    deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    if (choice.outcome === "accepted") setInstalled(true);
    return choice;
  }, [deferred]);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {}
  }, []);

  return {
    canInstall: !!deferred,
    isIos: detectIos(),
    isStandalone,
    installed,
    install,
    dismiss,
    dismissedRecently: isDismissedRecently(),
  };
}
